<template>
  <CloseHeader />

  <div class="app-container one-page">
    <div class="user-add-con">
      <h1 class="app-tit">{{ t('label_add_user') }}</h1>
      <ul class="add-input-wrap">
        <li>
          <span>ID</span>
          <input
            v-model="form.loginId"
            v-cursor-end
            class="app-t-input"
            type="text"
            :placeholder="t('alert_enter_id')"
            :disabled="!isCreateMode"
            enterkeyhint="next"
            @keyup.enter.prevent="refInputName?.focus()"
          />
        </li>
        <li>
          <span>{{ t('label_name') }}</span>
          <input
            ref="refInputName"
            v-model="form.nickname"
            v-cursor-end
            class="app-t-input"
            type="text"
            :placeholder="t('alert_enter_name')"
            enterkeyhint="next"
            @keyup.enter.prevent="refInputPassword?.focus()"
          />
        </li>
        <li>
          <span>{{ t('label_password') }}</span>
          <div class="password-wrap">
            <input
              ref="refInputPassword"
              v-model="form.password"
              v-cursor-end
              class="app-t-input"
              :type="showPassword[0] ? 'text' : 'password'"
              :placeholder="t('input_login_pw')"
              enterkeyhint="next"
              @keyup.enter.prevent="refInputPasswordConfirm?.focus()"
            />
            <button type="button" class="pw-view" @click="togglePassword(0)">
              <img
                :class="`${showPassword[0] ? 'view' : ''}`"
                :src="getAssetImageUrl(`common/icons/${showPassword[0] ? 'view' : 'hide'}-icon.svg`)"
              />
            </button>
          </div>
        </li>
        <li>
          <span>{{ t('alert_confirm_password') }}</span>
          <div class="password-wrap">
            <input
              ref="refInputPasswordConfirm"
              v-model="form.confirmPassword"
              v-cursor-end
              class="app-t-input"
              :type="showPassword[1] ? 'text' : 'password'"
              :placeholder="t('alert_reenter_password')"
              enterkeyhint="done"
              @keyup.enter.prevent="(e) => (e.target as HTMLInputElement).blur()"
            />
            <button type="button" class="pw-view" @click="togglePassword(1)">
              <img
                :class="`${showPassword[1] ? 'view' : ''}`"
                :src="getAssetImageUrl(`common/icons/${showPassword[1] ? 'view' : 'hide'}-icon.svg`)"
              />
            </button>
          </div>
        </li>
      </ul>
    </div>

    <div class="app-btn-wrap mt-8">
      <button v-if="isCreateMode" type="button" class="gray-btn l-btn" @click="goBack">
        <span>{{ t('button_cancel') }}</span>
      </button>
      <button v-else type="button" class="gray-btn l-btn" @click="openDeletePopup">
        <span>{{ t('delete_voice') }}</span>
      </button>
      <button type="button" class="primary-btn l-btn" @click="onSave">
        <span>{{ t(buttonLabel) }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { createEmployee, deleteEmployee, fetchPublicKey, updateEmployee } from '@/api/apiService';
import CloseHeader from '@/components/domains/mobile/common/CloseHeader.vue';
import Modal from '@/components/domains/mobile/common/PopupModal.vue';
import { setIsBack } from '@/router';
import { usePopupStore } from '@/stores/popup';
import { useToastStore } from '@/stores/toast';
import { encryptPassword } from '@/utils/encryptor';
import { ERROR_CODE } from '@/utils/enums';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';
import { validateId, validateName, validatePassword, validatePasswordConfirm } from '@/utils/validators';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const popupStore = usePopupStore();
const toastStore = useToastStore();

const isCreateMode = ref<boolean>(true);
const showPassword = ref<boolean[]>([false, false]);
const refInputName = useTemplateRef<HTMLInputElement>('refInputName');
const refInputPassword = useTemplateRef<HTMLInputElement>('refInputPassword');
const refInputPasswordConfirm = useTemplateRef<HTMLInputElement>('refInputPasswordConfirm');
const form = ref({
  userId: '',
  loginId: '',
  nickname: '',
  password: '',
  confirmPassword: '',
});

const buttonLabel = computed(() => (isCreateMode.value ? 'label_add' : 'button_edit'));

const goBack = () => {
  setIsBack(true);
  router.back();
};

const togglePassword = (index: number) => {
  showPassword.value[index] = !showPassword.value[index];
};

const openDeletePopup = () => {
  popupStore.open(Modal, {
    title: t('question_delete'),
    subTitle: t('alert_delete_irreversible'),
    iconSlot: h('img', {
      src: getAssetImageUrl('app/status/delect.svg'),
      alt: 'delete_icon',
    }),
    cancelButton: { text: t('button_cancel') },
    confirmButton: { text: t('delete_voice'), action: deleteUser },
  });
};

const deleteUser = async () => {
  const { success } = await deleteEmployee(form.value.userId);

  if (success) {
    setIsBack(true);
    router.back();
  }
};

const onSave = () => {
  if (!validate()) {
    return;
  }

  if (isCreateMode.value) {
    addUser();
  } else {
    editUser();
  }
};

const addUser = async () => {
  try {
    const { loginId, nickname } = form.value;
    const password = await getEncryptedPassword(form.value.password);

    const resCreateEmployee = await createEmployee({
      userId: loginId,
      nickname,
      username: nickname,
      password,
      groupIds: [],
      languageCode: 'ko',
    });

    if (resCreateEmployee.success) {
      toastStore.info(t('alert_create_success'));
      setIsBack(true);
      router.back();
    } else if (resCreateEmployee.errorCode === ERROR_CODE.ALREADY_EXISTS_USER_ID) {
      toastStore.error(t('alert_duplicate_id'));
    } else {
      toastStore.error(t('UNKNOWN_ERROR'));
    }
  } catch (error) {
    console.error('사용자 추가에 실패했습니다.', error);
  }
};

const editUser = async () => {
  try {
    const { userId, nickname } = form.value;
    const password = await getEncryptedPassword(form.value.password);

    const { success } = await updateEmployee(userId, password, {
      body: JSON.stringify({
        nickname,
        groupIds: [],
        languageCode: 'ko',
        sampleRate: '16000',
      }),
      voice: '',
    });

    if (success) {
      setIsBack(true);
      router.back();
    }
  } catch (error) {
    console.error(error);
  }
};

const validate = (): boolean => {
  const validators = [
    () => validateId(form.value.loginId),
    () => validateName(form.value.nickname),
    () => validatePassword(form.value.password),
    () => validatePasswordConfirm(form.value.password, form.value.confirmPassword),
  ];

  for (const func of validators) {
    const message = func();

    if (message) {
      popupStore.openToast(t(message));
      return false;
    }
  }

  return true;
};

const getEncryptedPassword = async (password: string) => {
  const { data: publicKeyData } = await fetchPublicKey();
  return await encryptPassword(publicKeyData.partnerPublicKey, password);
};

onMounted(() => {
  const { userId, loginId, nickname } = route.query;

  if (userId) {
    isCreateMode.value = false;
    form.value.userId = String(userId);
    form.value.loginId = String(loginId);
    form.value.nickname = String(nickname);
  }
});
</script>

<style scoped lang="scss">
.user-add-con {
  .add-input-wrap {
    padding-top: $size-m;
    li {
      &:not(:first-child) {
        margin-top: 1.5rem;
      }
      span {
        font-size: $font-size-xs;
        display: block;
        padding-bottom: $size-s;
      }
    }
  }
}
</style>
